/**
 * Lecture d'un ticket ou d'une facture, à partir de son texte.
 *
 * <p>Sans le Pack IA, une dépense prise en photo restait vide : la pièce était
 * jointe, mais ni le prix, ni la date, ni le commerce n'étaient repris. Le
 * texte est lu dans le téléphone (lib/ocr-ticket.js) et analysé ici : total
 * payé, date, nom et adresse du commerce, TVA, IBAN et catégorie, que ce soit
 * un restaurant, une station-service, un billet d'avion ou un loyer.
 *
 * <p>Le résultat a la forme de la lecture du Pack IA, pour remplir le même
 * formulaire. Ce qui n'est pas lu clairement vaut null : mieux vaut un champ
 * vide qu'un faux montant.
 */

import { PAYS_BY } from '../constants/countries';
import { paiementDuTicket } from './paiement-depense';

/** Taux de TVA suisses, actuels et précédents. */
const TAUX_SUISSES = [8.1, 2.6, 3.8, 7.7, 2.5, 3.7];

/**
 * Les taux qu'un ticket peut imprimer : les suisses, et ceux du pays de la
 * société (adresse réglée dans Réglages) — 20 % en France, 19 % en Allemagne.
 *
 * @param {string} [pays] code du pays de la société
 * @returns {number[]} les taux admis
 */
function tauxAdmis(pays) {
  const siens = (PAYS_BY[pays]?.tva || []).filter((t) => t > 0);
  return [...new Set([...TAUX_SUISSES, ...siens])];
}

/** Le texte en minuscules, sans accents, pour les mots-clés. */
const simple = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Un montant écrit à la suisse : 1'234.50, 1 234,50, 45.–, 12,50. Une date
 * (02.10.2026) n'en est pas un : « 02.10 » se lisait comme 2.10 francs. La
 * photo d'un ticket sépare parfois les centimes du point (« 3. 40 ») : c'est
 * toujours 3.40.
 */
const MONTANT =
  /(?<![\d.,'’])(\d{1,3}(?:['’ ]\d{3})+|\d+)[.,] ?(\d{2}|[-–]{1,2})(?![\d]|[.,/] ?\d)/g;

/**
 * Les montants d'une ligne.
 *
 * @param {string} ligne texte
 * @returns {number[]} les montants, dans l'ordre
 */
export function montantsDe(ligne) {
  const sortie = [];
  for (const m of String(ligne || '').matchAll(MONTANT)) {
    const entier = m[1].replace(/['’ ]/g, '');
    const cts = /\d{2}/.test(m[2]) ? m[2] : '00';
    const v = Number(entier + '.' + cts);
    if (Number.isFinite(v)) sortie.push(v);
  }
  return sortie;
}

const MOT_TOTAL =
  /\b(total|totale|summe|gesamt\w*|montant|a payer|zu zahlen|zahlbetrag|importo|amount|net a payer|solde|\w*betrag)\b/;
const MOT_PAIEMENT =
  /\b(bar|cash|especes?|bargeld|contanti|carte|karte|card|twint|maestro|visa|mastercard|postcard|debit)\b/;
/** « Total TTC », « Rechnungsbetrag inkl. MwSt » : le total, TVA comprise. */
const TVA_COMPRISE = /(inkl|incl|compris|ttc|including)/;
const MOT_EXCLU =
  /(sous-?total|subtotal|zwischensumme|rendu|monnaie|ruckgeld|retour|gegeben|erhalten|change|pourboire|trinkgeld|mwst|tva|iva|vat|mehrwert|umsatzsteuer|\bust\b|\bbtw\b|\bmoms\b|\bmva\b|\balv\b|\bdph\b|\bddv\b|\bpdv\b|\bptu\b|\bkdv\b|\bfpa\b|\bafa\b|\bpvm\b|\bpvn\b|\bdds\b|\bgst\b|exkl|hors taxe|\bht\b|netto)/;
// La TVA dans les langues des pays des sociétés : MwSt, USt, BTW, moms…
const MOT_TVA =
  /(mwst|tva|iva|vat|mehrwert|umsatzsteuer|\bust\b|\bbtw\b|\bmoms\b|\bmva\b|\balv\b|\bdph\b|\bddv\b|\bpdv\b|\bptu\b|\bkdv\b|\bfpa\b|\bafa\b|\bpvm\b|\bpvn\b|\bdds\b|\bgst\b)/;

/** Le total payé : la ligne « Total », sinon le plus grand montant. */
function total(lignes) {
  const candidats = [];
  lignes.forEach((l, i) => {
    const s = simple(l);
    if (MOT_EXCLU.test(s) && !(MOT_TOTAL.test(s) && TVA_COMPRISE.test(s) && !/%/.test(l))) return;
    const montants = montantsDe(l);
    if (!montants.length) return;
    const poids = MOT_TOTAL.test(s) ? 3 : MOT_PAIEMENT.test(s) ? 2 : 0;
    if (poids) candidats.push({ poids, v: Math.max(...montants), i });
  });
  if (candidats.length) {
    const meilleur = Math.max(...candidats.map((c) => c.poids));
    return Math.max(...candidats.filter((c) => c.poids === meilleur).map((c) => c.v));
  }
  const tous = lignes
    .filter((l) => !MOT_EXCLU.test(simple(l)) && !/%/.test(l))
    .flatMap((l) => montantsDe(l));
  return tous.length ? Math.max(...tous) : null;
}

const MOIS = {
  jan: 1,
  janv: 1,
  janvier: 1,
  januar: 1,
  january: 1,
  gennaio: 1,
  fev: 2,
  fevr: 2,
  fevrier: 2,
  feb: 2,
  februar: 2,
  february: 2,
  febbraio: 2,
  mar: 3,
  mars: 3,
  marz: 3,
  march: 3,
  marzo: 3,
  avr: 4,
  avril: 4,
  apr: 4,
  april: 4,
  aprile: 4,
  mai: 5,
  may: 5,
  maggio: 5,
  juin: 6,
  jun: 6,
  juni: 6,
  june: 6,
  giugno: 6,
  juil: 7,
  juillet: 7,
  jul: 7,
  juli: 7,
  july: 7,
  luglio: 7,
  aout: 8,
  aug: 8,
  august: 8,
  agosto: 8,
  sept: 9,
  sep: 9,
  septembre: 9,
  september: 9,
  settembre: 9,
  oct: 10,
  octobre: 10,
  okt: 10,
  oktober: 10,
  october: 10,
  ottobre: 10,
  nov: 11,
  novembre: 11,
  november: 11,
  dec: 12,
  decembre: 12,
  dez: 12,
  dezember: 12,
  december: 12,
  dicembre: 12,
};

const iso = (a, m, j) => a + '-' + String(m).padStart(2, '0') + '-' + String(j).padStart(2, '0');

/** La première date plausible du ticket, au format AAAA-MM-JJ. */
function date(texte, maintenant) {
  const anneeMax = maintenant.getFullYear() + 1;
  const valide = (a, m, j) => a >= 2000 && a <= anneeMax && m >= 1 && m <= 12 && j >= 1 && j <= 31;
  const trouvees = [];
  for (const m of texte.matchAll(/\b(\d{1,2})[./-](\d{1,2})[./-](\d{4}|\d{2})\b/g)) {
    let a = Number(m[3]);
    if (a < 100) a += 2000;
    if (valide(a, Number(m[2]), Number(m[1]))) trouvees.push({ i: m.index, d: iso(a, m[2], m[1]) });
  }
  for (const m of texte.matchAll(/\b(\d{4})-(\d{2})-(\d{2})\b/g)) {
    if (valide(Number(m[1]), Number(m[2]), Number(m[3])))
      trouvees.push({ i: m.index, d: iso(m[1], m[2], m[3]) });
  }
  const s = simple(texte);
  for (const m of s.matchAll(/\b(\d{1,2})\.?\s+([a-z]{3,9})\.?\s+(\d{4})\b/g)) {
    const mois = MOIS[m[2]];
    if (mois && valide(Number(m[3]), mois, Number(m[1])))
      trouvees.push({ i: m.index, d: iso(m[3], mois, m[1]) });
  }
  if (!trouvees.length) return null;
  const aujourdhui = iso(maintenant.getFullYear(), maintenant.getMonth() + 1, maintenant.getDate());
  const passees = trouvees.filter((t) => t.d <= aujourdhui);
  return (passees.length ? passees : trouvees).sort((x, y) => x.i - y.i)[0].d;
}

// « Page 1/2 » en tête d'une facture de plusieurs pages n'est pas le
// fournisseur : la facture HGC se lisait « Page ».
// Les étiquettes d'une facture ne le sont pas non plus : « Destinataire »
// au-dessus de notre adresse, « N° client », « Bulletin de livraison », l'en-tête
// du tableau — la facture HGC se lisait « Destinataire ».
const PAS_UN_NOM =
  /^(ticket|quittung|beleg|recu|re[cç]u|facture|rechnung|invoice|receipt|kassenbon|scontrino|date|datum|tel|tél|www|http|mwst|tva|uid|che-|no |nr|n°|n o|page\b|seite\b|pagina\b|blatt\b|destinataire|destinatario|empfanger|expediteur|absender|mittente|adresse\b|adresse de|lieferadresse|rechnungsadresse|livraison|livre a|lieu de livraison|client|kunde|cliente|customer|bill to|ship to|sold to|deliver|factur[ée] a|votre|notre|ihre|unsere|vostr|nostr|pos\b|pos\.|article|artikel|designation|bezeichnung|commande|bestellung|ordine|bulletin|bon de|lieferschein|copie|duplicata|original|kopie|concerne|objet|betreff|reference|ref\b|ref\.|a separer|a detacher|abtrennen|vor der einzahlung|section paiement|recepisse|empfangsschein|zahlteil|ricevuta|sezione pagamento)/i;

// Le texte imprimé sur le bulletin de versement, où qu'il tombe dans la ligne :
// « À détacher avant de procéder au paiement » se lisait « À r avant de
// procédérau paiement », et devenait le nom du bailleur du dépôt.
const TEXTE_DU_BULLETIN =
  /(avant de proc|vor der einzahlung|abzutrennen|prima del versamento|staccare|section paiement|recepisse|empfangsschein|zahlteil|sezione pagamento)/i;

// La forme juridique qui suit un nom de société, au pied d'une facture.
const FORME_JURIDIQUE =
  /\b(s\.?a\.?|ag|s[àa]rl|gmbh|soci[ée]t[ée] coop[ée]rative|genossenschaft|snc|ltd|sas|srl|spa|cie)\s*$/i;

/** Le nom du commerce : la première ligne de lettres en tête du ticket. */
function fournisseur(lignes) {
  for (const l of lignes.slice(0, 8)) {
    const propre = l
      .replace(/[|_*#=~<>]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const lettres = (propre.match(/[A-Za-zÀ-ÿ]/g) || []).length;
    if (lettres < 3 || lettres < propre.length * 0.5) continue;
    if (PAS_UN_NOM.test(simple(propre))) continue;
    if (TEXTE_DU_BULLETIN.test(simple(propre))) continue;
    if (montantsDe(propre).length) continue;
    return propre.slice(0, 80);
  }
  return null;
}

/**
 * Le fournisseur, quand l'en-tête n'imprime pas son nom en lettres (un logo) :
 * le pied de la facture le dit (« HG COMMERCIALE · Société coopérative ·
 * Ch. … · 1227 Carouge · www.hgc.ch »). La société d'abord, avec sa forme
 * juridique ; sinon le nom du site internet.
 *
 * @param {string[]} lignes lignes de la facture, sans les nôtres
 * @returns {{nom: string, ligne: string}|null} le nom et la ligne où il est lu
 */
function fournisseurDuPied(lignes) {
  for (const l of lignes) {
    const morceaux = l
      .split(/\s*[·•|]\s*|\s{3,}/)
      .map((m) => m.trim())
      .filter(Boolean);
    for (let i = 0; i < morceaux.length; i += 1) {
      const m = morceaux[i];
      if (!FORME_JURIDIQUE.test(m) || montantsDe(m).length || /\d{3}/.test(m)) continue;
      // « Société coopérative » seule : le nom est juste avant.
      const seule = m.replace(FORME_JURIDIQUE, '').trim();
      const nom = seule.length >= 2 ? m : i > 0 ? morceaux[i - 1] : '';
      if (nom && !PAS_UN_NOM.test(simple(nom)) && (nom.match(/\p{L}/gu) || []).length >= 3)
        return { nom: nom.slice(0, 80), ligne: l };
    }
  }
  for (const l of lignes) {
    const site = l.match(/\bwww\.([a-z0-9-]{2,30})\.[a-z]{2,}/i);
    if (!site) continue;
    const d = site[1];
    return {
      nom: d.length <= 4 ? d.toUpperCase() : d[0].toUpperCase() + d.slice(1).toLowerCase(),
      ligne: l,
    };
  }
  return null;
}

/** L'adresse imprimée sur la ligne du pied : « Ch. de l'Exemple 1 · 1227 Carouge ». */
function adresseDuPied(ligne) {
  const morceaux = ligne.split(/\s*[·•|,]\s*|\s{3,}/).map((m) => m.trim());
  const i = morceaux.findIndex((m) => /^(?:CH-)?[1-9]\d{3}\s+\p{L}/u.test(m));
  if (i < 0) return null;
  const rue = i > 0 && RUE.test(simple(morceaux[i - 1])) ? morceaux[i - 1] + ', ' : '';
  return rue + morceaux[i].replace(/^CH-/, '');
}

const RUE =
  /\b(rue|route|rte|chemin|ch\.|avenue|av\.|place|pl\.|boulevard|bd|quai|allee|impasse|strasse|str\.|weg|gasse|platz|via|viale|piazza|corso)\b|\d+[a-z]?\s*$|^\s*\d+/i;

/** L'adresse du commerce : rue et numéro, NPA et localité, sur une ligne. */
function adresse(lignes) {
  for (let i = 0; i < Math.min(lignes.length, 14); i += 1) {
    // « Objet : Mme Exemple, Route du Lac 1, 1228 Plan-les-Ouates » : l'adresse
    // du chantier, pas celle du fournisseur.
    if (/^(objet|betreff|oggetto|concerne|subject)\b/.test(simple(lignes[i]))) continue;
    // « Date 28.09.2026 Votre commande » : l'année d'une date n'est pas un NPA.
    const m = lignes[i].match(
      /(?<![\d./-])\b(?:CH-)?([1-9]\d{3})\s+([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ .'-]{1,40})/,
    );
    if (!m) continue;
    const localite = (m[1] + ' ' + m[2]).replace(/\s+/g, ' ').trim();
    const avant = i > 0 ? lignes[i - 1].trim() : '';
    // « ROUTE DE ST JULIEN 286 - 1258 PERLY » : le tiret avant le NPA tombe,
    // l'adresse se lisait « 286 -, 1258 PERLY ».
    const debut = lignes[i]
      .slice(0, m.index)
      .replace(/[\s,;:=\-–—]+$/, '')
      .trim();
    const rue =
      debut && RUE.test(simple(debut)) ? debut : avant && RUE.test(simple(avant)) ? avant : '';
    return { adresse: (rue ? rue + ', ' : '') + localite, localite: m[2].trim() };
  }
  return { adresse: null, localite: null };
}

/** Un taux admis écrit « 8.1 », « 8,10 » ou « 20 », ou null. */
function tauxConnu(texte, admis) {
  const t = Number(String(texte).replace(',', '.'));
  return admis.includes(t) ? t : null;
}

/** Arrondi au centime. */
const auCentime = (v) => Math.round(v * 100) / 100;

/**
 * La TVA d'une ligne à un taux : le montant qui vaut ce taux d'un autre
 * montant de la ligne, hors taxe (« 8.1% de 2'000.00 162.00 ») ou TVA comprise
 * (« 2.6% 5.46 0.14 5.60 »). À défaut, sur une ligne « TVA », le plus petit.
 */
function tvaDeLaLigne(taux, montants, plafond, ligneTva) {
  const juste = (v) =>
    montants.some(
      (x) =>
        x !== v &&
        (Math.abs(auCentime((x * taux) / 100) - v) < 0.015 ||
          Math.abs(auCentime((x * taux) / (100 + taux)) - v) < 0.015),
    );
  const verifies = montants.filter((v) => v > 0 && juste(v));
  if (verifies.length) return Math.min(...verifies);
  if (!ligneTva) return null;
  const valeurs = montants.filter((v) => v > 0 && v <= plafond);
  return valeurs.length ? Math.min(...valeurs) : null;
}

/**
 * Les taux et le total de TVA imprimés : une ligne par taux (« TVA 8,1% 1.21 »),
 * un tableau (Migros, Coop : taux, base, TVA, brut) ou un total « Total TVA ».
 * Avec deux taux, la TVA est la somme des deux ; une même ligne répétée en
 * « Total TVA » n'est comptée qu'une fois.
 */
function tva(lignes, ttc, admis, tous = []) {
  // La TVA ne dépasse pas la part du plus haut taux admis dans le total.
  const plus = Math.max(...admis);
  const plafond = ttc == null ? Infinity : (ttc * plus) / (100 + plus) + 0.01;
  const taux = new Set();
  const parTaux = new Map();
  const totaux = [];
  let tableau = false;
  for (const l of lignes) {
    const s = simple(l);
    const ligneTva = MOT_TVA.test(s);
    const avecPourcent = [...l.matchAll(/(\d{1,2}(?:[.,]\d{1,2})?)\s*%/g)]
      .map((m) => tauxConnu(m[1], admis))
      .filter((t) => t != null);
    // Une ligne de tableau commence par le taux, sans « % » (Coop : « 2.60 5.80 0.15 »).
    const enTete = tableau && !avecPourcent.length && l.match(/^\s*(\d{1,2}[.,]\d{1,2})\s+\d/);
    const tauxDeLaLigne = avecPourcent.length
      ? avecPourcent
      : enTete
        ? [tauxConnu(enTete[1], admis)].filter(Boolean)
        : [];
    if (ligneTva && /(satz|basis|base|netto|brutto|taux|%)/.test(s) && !montantsDe(l).length) {
      tableau = true;
      continue;
    }
    if (!ligneTva && !tauxDeLaLigne.length && !(tableau && MOT_TOTAL.test(s))) {
      if (montantsDe(l).length) tableau = false;
      continue;
    }
    // « DONT TVA 8.10 0.25 » : le taux sans « % », puis la TVA. Il n'est pris
    // pour un taux que si la TVA qui le suit est celle d'un montant du ticket :
    // « MWST 7.70 CHF 107.70 » reste un montant de TVA.
    if (ligneTva && !tauxDeLaLigne.length) {
      const [premier, ...suite] = montantsDe(l);
      const r = premier == null ? null : tauxConnu(premier, admis);
      const v =
        r == null
          ? null
          : suite.find(
              (x) =>
                x > 0 && tous.some((t) => Math.abs(auCentime((t * r) / (100 + r)) - x) < 0.006),
            );
      if (v != null) {
        taux.add(r);
        if (!parTaux.has(r)) parTaux.set(r, v);
        continue;
      }
      // La TVA à côté mal lue (« 0.5% », « 03 ») : le taux reste, s'il n'est
      // pas lui-même la TVA d'un montant du ticket.
      const estUneTva = (x) =>
        admis.some((t) => tous.some((m) => Math.abs(auCentime((m * t) / (100 + t)) - x) < 0.006));
      if (r != null && !estUneTva(premier)) {
        taux.add(r);
        continue;
      }
    }
    tauxDeLaLigne.forEach((t) => taux.add(t));
    let reste = l.replace(/\d{1,2}(?:[.,]\d{1,2})?\s*%/g, ' ');
    if (enTete) reste = reste.replace(enTete[1], ' ');
    const montants = montantsDe(reste);
    if (!montants.length) continue;
    if (tauxDeLaLigne.length === 1) {
      const v = tvaDeLaLigne(tauxDeLaLigne[0], montants, plafond, ligneTva);
      if (v != null && !parTaux.has(tauxDeLaLigne[0])) parTaux.set(tauxDeLaLigne[0], v);
    } else if (!tauxDeLaLigne.length && (MOT_TOTAL.test(s) || tableau || ligneTva)) {
      totaux.push(montants.filter((v) => v > 0 && v <= plafond));
    }
  }
  const somme = parTaux.size ? auCentime([...parTaux.values()].reduce((a, v) => a + v, 0)) : null;
  // Un total imprimé fait foi quand il confirme la somme, ou quand il n'y a qu'elle.
  const imprimes = totaux.flat();
  let montantTva = somme;
  if (somme == null && imprimes.length === 1) [montantTva] = imprimes;
  return { tauxTva: [...taux], montantTva };
}

/** Le numéro de la facture : « Facture n° D2026-059 », « Rechnung Nr. 4711 ». */
function numero(texte) {
  const m = String(texte || '').match(
    /\b(?:facture|rechnung|invoice|fattura|bon|re[cç]u|quittung|beleg|note)\s*(?:n[°o]\.?|nr\.?|no\.?|num[ée]ro|#)?\s*:?\s*([A-Z]{0,4}[-/]?\d[A-Z0-9\-/.]{1,19})/i,
  );
  return m ? m[1].replace(/[.\-/]+$/, '') : null;
}

/**
 * Le nom du fournisseur séparé de son adresse, quand la facture les imprime
 * sur la même ligne.
 *
 * @param {string|null} texte nom lu en tête
 * @returns {{nom: string|null, reste: string|null}} le nom, et l'adresse qui suivait
 */
function nomSansAdresse(texte) {
  if (!texte) return { nom: texte, reste: null };
  // Les formes de société des pays proposés : « SA », « GmbH », « Ltd »,
  // « BV », « sp. z o.o. », « LLC »…
  const forme = String(texte).match(
    /^(.*?\b(?:s\.?a\.?(?:\s?u\.?)?|ag|s[àa]rl|sagl|gmbh|kg|ohg|e\.\s?u\.|snc|sas|sasu|eurl|eirl|srl|s\.r\.l\.|spa|s\.p\.a\.|s\.l\.|ltda|lda|ltd|llc|inc|corp|plc|pty|bv|b\.v\.|nv|n\.v\.|ab|as|a\/s|aps|oy|oyj|kft|zrt|s\.r\.o\.|a\.s\.|sp\.\s?z\s?o\.o\.|d\.o\.o\.|d\.d\.|ooo|ооо|ao|jsc|bhd|sdn\s+bhd|pte|kk|co\.?,?\s+ltd))\s*,?\s+(.*\d.*)$/iu,
  );
  // Le début d'une rue, dans les langues des pays proposés.
  const rue = String(texte).match(
    /^(.*?\p{L}.*?)\s+((?:rue|route|rte|chemin|ch\.|avenue|av\.|boulevard|bd|place|quai|impasse|all[ée]e|strasse|straße|str\.|weg|gasse|platz|via|viale|piazza|corso|calle|avenida|av\.|rua|travessa|straat|laan|plein|ulica|ul\.|utca|gatan|gata|vej|gade|vei|katu|street|st\.|road|rd\.|lane|drive|ul\.|ulitsa|ул\.?|cesta|ulice|sokak|cad\.|caddesi)\b.*\d.*)$/iu,
  );
  const m = forme || rue;
  if (!m || m[1].trim().length < 3) return { nom: texte, reste: null };
  const reste = m[2].replace(/(\d+[a-z]?)\s+(\d{4,5}\s)/i, '$1, $2').trim();
  return { nom: m[1].trim(), reste };
}

/** IBAN suisse imprimé (QR-facture, coordonnées de paiement). */
function iban(texte) {
  const m = texte
    .replace(/\s+/g, ' ')
    .match(/\b(CH|LI)\s?(\d{2})((?:\s?[0-9A-Z]{4}){4}\s?[0-9A-Z])\b/);
  return m ? (m[1] + m[2] + m[3]).replace(/\s/g, '') : null;
}

/**
 * Mots-clés de chaque catégorie, la plus précise d'abord : une location de
 * voiture à l'aéroport n'est pas un billet d'avion, la Banque Cantonale
 * Vaudoise pas une assurance, l'« Avis de débit » de la banque pas Avis.
 */
const CATEGORIES = [
  // La caisse AVS qui facture les salaires des employés (cotisations
  // paritaires, masse salariale) : pas l'AVS personnelle du patron.
  [
    'charges_sociales',
    /(cotisations? paritaires?|masse salariale|lohnsumme|paritatische beitr|decompte (?:des |de )?salaires|lohnbescheinigung)/,
  ],
  // Payé à l'État : avant « cotisation » (formation) et « assurance ».
  [
    'etat_avs',
    /(caisse (?:cantonale )?(?:genevoise )?de compensation|ausgleichskasse|cotisations? (?:avs|personnelles)|acompte(?:s)? (?:de cotisations? )?avs|\bahv\b|\bccgc\b|fer ciam|cicicam)/,
  ],
  [
    'etat_tva',
    /(administration federale des contributions|eidgenossische steuerverwaltung|decompte (?:de )?tva|mwst-?abrechnung|acompte tva|\bestv\b|\bafc\b[^\n]*\btva\b|\btva\b[^\n]*\bafc\b)/,
  ],
  [
    'etat_impots',
    /(administration fiscale|steuerverwaltung|bordereau d.impots?|acompte d.impots?|impots? (?:cantonaux|communaux|federal direct|sur le revenu)|steuerrechnung|\b(?:acompte )?icc\b|\bifd\b|(?:etat de [a-z]+|\bafc\b|canton|commune)[^\n]*\bimpots?\b|\bafc\b[^\n]*acompte|steueramt)/,
  ],
  // La caisse de pension (2ᵉ pilier) : avant « assurance » et « fondation ».
  [
    'lpp',
    /(sammelstiftung|fondation collective|fondation de prevoyance|caisse de pensions?|pensionskasse|vorsorgestiftung|\blpp\b|\bbvg\b|nest stiftung|\bnest\b)/,
  ],
  [
    'perte_gain',
    /(perte de gain|indemnites? journalieres|\bijm\b|krankentaggeld|taggeldversicherung)/,
  ],
  [
    'assurance_vehicule',
    /(assurance (?:vehicule|auto|automobile|des vehicules)|vehicules a moteur|motorfahrzeugversicherung|autoversicherung|\bcasco\b)/,
  ],
  [
    'loc_voiture',
    /(hertz|europcar|\bsixt\b|\bavis (?:rent|budget)|mietwagen|location de voiture|car rental)/,
  ],
  // Le stationnement avant l'avion et le logement : « Parking Aéroport » n'est
  // pas un billet d'avion, ni « Parking Hôtel-de-Ville » un logement.
  [
    'stationnement',
    /(parking|parkhaus|parkplatz|parcom[eè]tre|place de parc|stationnement|parkuhr|easypark|parkingpay|onepark|parcheggio|aparcamiento|estacionamento|parkim)/,
  ],
  [
    'billets_avion',
    /(easyjet|ryanair|lufthansa|air france|swiss international|flyswiss|edelweiss|klm|boarding|carte d'embarquement|e-ticket|flight|airline|\bvol\b|\bpnr\b|(?<!avia |shell |tamoil |bp |agip |eni |esso |socar |migrol |agrola |pronto )(?:aeroport|flughafen))/,
  ],
  [
    'carburant',
    /(essence|diesel|benzin|bleifrei|sans plomb|carburant|tankstelle|station-service|\bliter\b|\blitres?\b|gasolina|gasoleo|gasolinera|combustivel|combustible|carburante|\bfuel\b|karburant|benzinska)/,
  ],
  [
    'achat_vehicule',
    /(contrat de vente|vente (?:de )?vehicule|kaufvertrag|numero de chassis|fahrgestell|vehicule d.occasion|vehicule neuf)/,
  ],
  ['leasing', /leasing/],
  [
    'interets',
    /(decompte d.interets|interets? (?:debiteurs?|crediteurs?|du credit|hypothecaires?)|zinsabrechnung|sollzins|schuldzins)/,
  ],
  [
    'bancaire',
    /(frais bancaires|frais de tenue|tenue d[eu] compte|frais de virements?|commission bancaire|kontofuhrung|bankspesen|bankgebuhren|frais forfaitaires (?:du |de )?compte|cotisation annuelle(?: de)?(?: la)? carte|jahresgebuhr|kartengebuhr)/,
  ],
  [
    'voyage',
    /(agence de voyages?|reiseburo|hotelplan|\btui\b|kuoni|voyage d.affaires|forfait voyage)/,
  ],
  [
    'logement',
    /(hotel|booking\.com|airbnb|nuitee|ubernachtung|auberge|chambre|hostal|hostel|albergo)/,
  ],
  [
    'transports',
    /(\bcff\b|\bsbb\b|\bffs\b|\btpg\b|\btpn\b|\bbls\b|unireso|leman ?express|\bmobility\b|postauto|car postal|billet de train|\buber\b|\btaxi\b|peage|vignette|\bsncf\b|\brenfe\b|trenitalia|deutsche bahn|\bratp\b|\bbolt\b|\blyft\b|autostrada|autopista|maut)/,
  ],
  // Un café où l'on a bu (eau minérale, café, verre de vin) : « Café ». Un
  // café-restaurant qui sert des repas reste sous « Repas ».
  [
    'cafe',
    /^(?![\s\S]*(?:restaurant|ristorante|restaurante|restoran|pizzeria|trattoria|brasserie|plat du jour|menu du jour|couverts?))[\s\S]*(?:\bcafe\b|caffe|\bbar\b|tea-?room|starbucks|\bcoffee\b|\bkafe\b|kaffee|cafeteria|kahve|kawiarnia|kavana|koffie)/,
  ],
  [
    'repas',
    /(restaurant|ristorante|restaurante|restoran|restauracja|lokanta|taverna|gasthaus|eetcafe|pret a manger|\bcafe\b|caffe|bistro|brasserie|pizz|domino|trattoria|mcdonald|burger|kebab|sushi|subway|\bkfc\b|\bpoke\b|tea-?room|gaststatte|wirtschaft|couverts?|plat du jour|menu du jour|boisson|getranke)/,
  ],
  // La nourriture à emporter (boulangerie, boucherie, traiteur) : taux réduit.
  [
    'nourriture',
    /(boulangerie|patisserie|boucherie|traiteur|confiserie|gourmet|a l.emporter|take ?away|backerei|metzgerei|panaderia|pasteleria|padaria|panetteria|pasticceria|bakery|bakkerij|furre buke|pekara|piekarnia|\bfirin\b|brutarie)/,
  ],
  [
    'materiel',
    /(hornbach|bauhaus|jumbo|\bobi\b|\blandi\b|getaz|debrunner|richner|sabag|brico|baumarkt|quincaillerie|materiaux|baustoff|holz|bricolage|\bhgc\b|hg commerciale|coop bau|\bdo it\b|leroy merlin|castorama|\bpoint p\b|bauwerk|kuratle|tarkett|\bsfs\b|sanitas troesch|\bikea\b)/,
  ],
  [
    'outillage',
    /(wurth|hilti|makita|festool|metabo|outillage|werkzeug|perceuse|visseuse|meuleuse|\bscie\b|perforateur)/,
  ],
  // Les travaux d'une autre entreprise : plombier, électricien, peintre…
  // La facture du sous-traitant tombait sous « Loyer » (le « Point de dépôt »
  // du bulletin) ou « Communications » (le « Mobile : » du pied de page).
  [
    'soustraitance',
    /(sous-?traitan|main-?d.?(?:oe|œ)uvre|travaux de|prestations? a executer|plomberie|\bsanitaires?\b|electricien|installations? electrique|menuiserie|carrelage|maconnerie|platrerie|peinture|ferblanterie|couverture|charpente|\bpose d)/,
  ],
  [
    'epicerie',
    /(\bcoop\b(?! pronto| bau)|migros|migrolino|\bmmm?\b|denner|\baldi\b|\blidl\b|\bspar\b|\bvolg\b|manor food|epicerie|mercadona|carrefour|auchan|leclerc|intermarche|monoprix|franprix|\btesco\b|sainsbury|\basda\b|edeka|\brewe\b|kaufland|\bpenny\b|\bnetto\b|\bconad\b|esselunga|continente|pingo doce|albert heijn|biedronka|konzum|viva fresh|supermercado|supermercato|supermarket|supermarche|supermarkt|\bmarket\b)/,
  ],
  // Un contrat de bail (box, place, local loué) : « Location, bail ».
  ['location_bail', /(contrat de bail|bail a loyer|\bbail\b|mietvertrag)/],
  [
    'loyer',
    /(\bloyer\b|\bmiete\b|mietzins|affitto|location (?:du|d.un) depot|loyer du depot|entrepot|lagerraum)/,
  ],
  [
    'communications',
    /(swisscom|sunrise|\bsalt\b|\bwingo\b|\byallo\b|m-budget mobile|telephone mobile|internet|abonnement)/,
  ],
  [
    'energie',
    /(romande energie|\bsig\b|\bewz\b|electricite|\bstrom\b|\bgaz\b|\beau\b|chauffage|mazout)/,
  ],
  [
    'port',
    /(la poste|post ch|\bdhl\b|\bups\b|fedex|colis|paket|envoi (?:postal|recommande|de colis))/,
  ],
  [
    'assurance',
    /(assurance|versicherung|\baxa\b|mobiliar|mobiliere|helvetia|baloise|generali|vaudoise|allianz|zurich (?:assurance|versicherung|compagnie)|\bsuva\b|groupe mutuel|\bsmile\b|emmental)/,
  ],
  ['vehicule', /(garage|pneus|reifen|carrosserie|lavage|waschanlage|service auto)/],
  ['reparation', /(reparation|reparatur|depannage|entretien|revision|maintenance)/],
  ['menage', /(nettoyage|reinigung|menage|conciergerie)/],
  ['fournitures', /(papeterie|office world|fournitures|papier|cartouche|toner)/],
  [
    'sante',
    /(pharmacie|apotheke|farmacia|pharmacy|apoteka|eczane|medecin|arzt|dentiste|premiers secours)/,
  ],
  ['juridique', /(avocats?|notaire|honoraires|juridique|rechtsanwalt|anwalt|\bnotar\b)/],
  [
    'dechets',
    /(dechetterie|decheterie|dechets|centre de tri|benne|recyclage|entsorgung|mulde|deponie|gravats)/,
  ],
  [
    'formation',
    /(formation|seminaire|weiterbildung|\bkurs\b|cotisation|cours (?:de|sur|securite))/,
  ],
  [
    'admin',
    /(registre du commerce|emolument|office des poursuites|handelsregister|betreibungsamt|chancellerie|fiduciaire)/,
  ],
  ['pub', /(publicit|werbung|flyers?|inserat|annonce|google ads|local\.ch|affiches?|marketing)/],
  [
    'divertissements',
    /(bowling|cinema|\bkino\b|concert|theatre|ticketcorner|karting|escape game|divertissement|loisirs)/,
  ],
  [
    'vetements',
    /(vetements?|habits|chaussures|bekleidung|arbeitskleidung|workwear|workfashion|pantalon|\bveste\b|gants de travail)/,
  ],
  ['dons', /(attestation de don|votre don|\bdons?\b|spende)/],
  // Le nom de la station seul, en dernier : un café pris au Relais Tamoil
  // est un café, pas du carburant.
  [
    'carburant',
    /(\bshell\b|\bbp\b|migrol|\bavia\b|tamoil|\bagip\b|\beni\b|socar|coop pronto|\besso\b|totalenergies|\bruedi\b|piccadilly|\bavanti\b|\bagrola\b|repsol|\bcepsa\b|\bgalp\b|\bomv\b|\baral\b|\bq8\b)/,
  ],
];

/**
 * La catégorie d'une dépense d'après son texte (ticket, libellé du relevé
 * bancaire…), ou null quand aucun mot ne la dit.
 *
 * @param {string} texte le texte lu
 * @returns {string|null} la clé de la catégorie (FRAIS_CAT)
 */
export function categorieDuTexte(texte) {
  return categorie(texte);
}

/** La catégorie la plus proche, ou null. */
function categorie(texte) {
  const s = simple(texte);
  // Un bulletin de versement imprime « La Poste » ou « Point de dépôt » :
  // le loyer du dépôt payé par bulletin se rangeait en « Frais de port ».
  const bulletin =
    /(recepisse|section paiement|point de depot|payable a|a separer|a detacher|empfangsschein|zahlteil|annahmestelle|ricevuta|sezione pagamento)/.test(
      s,
    );
  const envoi = /(colis|paket|\bdhl\b|\bups\b|fedex|affranchissement|frais de port)/.test(s);
  const trouvee = CATEGORIES.find(
    ([cat, motif]) => (cat !== 'port' || !bulletin || envoi) && motif.test(s),
  );
  return trouvee ? trouvee[0] : null;
}

/**
 * Les lignes sans le bloc de la société qui reçoit la facture.
 *
 * <p>La facture d'un sous-traitant imprime l'adresse de son client — le
 * patron — souvent avant la sienne : sans ce tri, le patron devenait le
 * fournisseur de sa propre facture. Son nom, l'adresse qui le suit
 * (rue, NPA, pays) et le « Bonjour … » qui le salue sont écartés.
 *
 * @param {string[]} lignes lignes du texte
 * @param {string[]} noms noms de la société destinataire
 * @returns {string[]} les lignes restantes
 */
function sansNousMemes(lignes, noms) {
  const cles = (noms || [])
    .map((n) => simple(n).replace(/\s+/g, ' ').trim())
    .filter((n) => n.length >= 4);
  if (!cles.length) return lignes;
  const nous = (l) => {
    const s = simple(l).replace(/\s+/g, ' ');
    // La ligne commence par le nom de la société : « Plâtrerie Exemple Sàrl »
    // n'est pas « Exemple Sàrl ».
    return cles.some((c) => s === c || s.startsWith(c + ' ') || s.startsWith(c + ','));
  };
  const sortie = [];
  for (let i = 0; i < lignes.length; i += 1) {
    if (!nous(lignes[i])) {
      sortie.push(lignes[i]);
      continue;
    }
    // L'adresse qui suit le nom : rue, NPA et localité, pays.
    let j = i + 1;
    while (
      j < lignes.length &&
      j <= i + 3 &&
      (RUE.test(simple(lignes[j])) ||
        /\b(?:CH-)?[1-9]\d{3}\s+[A-Za-zÀ-ÿ]/.test(lignes[j]) ||
        /^(suisse|schweiz|svizzera|switzerland|france|italie|allemagne)$/i.test(lignes[j]))
    ) {
      j += 1;
    }
    i = j - 1;
  }
  return sortie;
}

/** Le nom est-il celui de la société qui reçoit la facture ? */
function estNous(nom, noms) {
  const s = simple(nom).replace(/\s+/g, ' ').trim();
  return (noms || []).some((n) => {
    const c = simple(n).replace(/\s+/g, ' ').trim();
    return c.length >= 4 && (s === c || s.startsWith(c + ' ') || s.startsWith(c + ','));
  });
}

// L'étiquette seule sur sa ligne, au-dessus de l'adresse du client.
const ETIQUETTE_DESTINATAIRE =
  /^(destinataire|destinatario|empfanger|adresse de livraison|adresse de facturation|lieferadresse|rechnungsadresse|indirizzo di consegna|livre a|lieu de livraison|facture a|bill to|ship to|sold to|deliver to|client|kunde|cliente|customer)\s*:?$/;

/**
 * Les lignes sans le bloc du destinataire : « Destinataire », puis le nom,
 * la rue et la localité du client — c'est nous, jamais le fournisseur, même
 * quand la facture écrit notre nom autrement que SwissTech (« BEKTECH
 * BEKTESHI » pour « Bektech Bekteshi Sàrl »).
 *
 * @param {string[]} lignes lignes de la facture
 * @returns {string[]} les lignes restantes
 */
function sansDestinataire(lignes) {
  const sortie = [];
  for (let i = 0; i < lignes.length; i += 1) {
    if (!ETIQUETTE_DESTINATAIRE.test(simple(lignes[i]).trim())) {
      sortie.push(lignes[i]);
      continue;
    }
    // Le nom et l'adresse, jusqu'à la ligne « NPA localité » (quatre lignes au plus).
    let j = i + 1;
    while (j < lignes.length && j <= i + 4) {
      const npa = /\b(?:CH-)?[1-9]\d{3}\s+[A-Za-zÀ-ÿ]/.test(lignes[j]);
      j += 1;
      if (npa) break;
    }
    i = j - 1;
  }
  return sortie;
}

/** Mots de deux lettres qui font partie d'un nom de commerce. */
const SIGLES = /^(sa|ag|co|cie|gmbh|sarl|sàrl|srl|spa|ltd|inc)$/i;

/**
 * Le nom ou l'adresse sans les signes parasites que la photo ajoute en fin de
 * ligne (le tapis sous le ticket lu comme « ss », « . 8 », « = »).
 *
 * @param {string|null} texte nom ou adresse lus
 * @returns {string|null} le texte propre
 */
function sansParasites(texte) {
  if (!texte) return texte;
  // « HGC Matériaux SA          Page 1/2 » : le numéro de page, sur la même
  // ligne que le nom, n'en fait pas partie.
  const mots = String(texte)
    .replace(/\s+(page|seite|pagina|blatt)\s*\d+\s*(\/|de|von|di|sur)\s*\d+\s*$/i, '')
    .trim()
    .split(/\s+/);
  while (mots.length > 1) {
    const m = mots[mots.length - 1];
    const parasite =
      /^[^\p{L}\p{N}]+$/u.test(m) ||
      /^\d$/.test(m) ||
      (/^\p{L}{1,2}[^\p{L}\p{N}]*$/u.test(m) && !SIGLES.test(m.replace(/[^\p{L}]/gu, '')));
    if (!parasite) break;
    mots.pop();
  }
  return mots.join(' ').replace(/[\s,;:=.]+$/u, '');
}

/** Chiffres que la photo d'un ticket confond (le 0 et le 8 en gras, surtout). */
const CONFONDUS = {
  0: '869',
  1: '74',
  3: '85',
  5: '63',
  6: '508',
  7: '1',
  8: '0369',
  9: '08',
};

/**
 * Le total, contrôlé par la TVA imprimée : « 16.20 » lu « 16.28 » donnait
 * 1.22 de TVA à 8.1 %, quand le ticket en imprime 1.21. Un chiffre souvent
 * confondu est essayé ; seul un total qui retombe sur la TVA du ticket remplace
 * celui qui a été lu.
 *
 * @param {number|null} lu total lu
 * @param {number[]} taux taux de TVA imprimés
 * @param {number|null} tvaLue montant de TVA imprimé
 * @param {number[]} [tous] tous les montants du ticket
 * @returns {number|null} le total
 */
function totalSelonLaTva(lu, taux, tvaLue, tous = []) {
  if (tvaLue == null || taux.length !== 1) return lu;
  const r = taux[0];
  const juste = (t) => Math.abs(Math.round(((t * r) / (100 + r)) * 100) / 100 - tvaLue) < 0.006;
  if (lu != null && juste(lu)) return lu;
  // Le total mal lu (« 3.40 » lu « 3.- ») : un montant du ticket qui retombe
  // sur la TVA imprimée, le prix de la ligne « 1 CAFE 3.40 ».
  // Un total lu plus haut reste : il peut ne pas tomber juste au centime.
  const justes = tous.filter((t) => t > tvaLue && juste(t) && (lu == null || t > lu));
  if (justes.length) return Math.max(...justes);
  if (lu == null) return lu;
  const chiffres = lu.toFixed(2);
  const trouves = new Set();
  for (let i = 0; i < chiffres.length; i += 1) {
    for (const autre of CONFONDUS[chiffres[i]] || '') {
      const t = Number(chiffres.slice(0, i) + autre + chiffres.slice(i + 1));
      if (t > 0 && juste(t)) trouves.add(t);
    }
  }
  return trouves.size === 1 ? [...trouves][0] : lu;
}

/** Les étiquettes du bulletin de versement QR, dans les quatre langues admises. */
const QR_CREANCIER =
  /^(compte \/ payable a|konto \/ zahlbar an|conto \/ pagabile a|account \/ payable to|payable a|zahlbar an|pagabile a|payable to)$/;
const QR_DEBITEUR = /^(payable par|zahlbar durch|pagabile da|payable by)$/;
const QR_MONTANT = /^(monnaie|wahrung|valuta|currency)\s+(montant|betrag|importo|amount)$/;
const QR_ETIQUETTE =
  /^(compte|konto|conto|account|reference|referenz|riferimento|informations supplementaires|zusatzliche informationen|informazioni supplementari|additional information|recepisse|empfangsschein|ricevuta|receipt|section paiement|zahlteil|sezione pagamento|payment part|point de depot|annahmestelle|punto di accettazione|acceptance point)\b/;

/**
 * Le bulletin de versement QR imprimé au bas d'une facture suisse : à qui
 * payer (le fournisseur), qui paie (nous) et le montant à payer. Il fait foi :
 * « Total 4'314.50 » avant les rabais se lisait pour le montant, et le nom du
 * client mal écrit (« Bektech bekteteshi ») pour le fournisseur.
 *
 * <p>Le texte d'un PDF met côte à côte le récépissé et la section paiement :
 * chaque morceau d'une ligne se lit pour lui-même.
 *
 * @param {string[]} lignes lignes de la facture, telles qu'imprimées
 * @returns {{creancier: string|null, debiteur: string|null, montant: number|null}}
 */
function bulletinQr(lignes) {
  const morceaux = lignes.map((l) =>
    l
      .split(/\s{3,}/)
      .map((m) => m.trim())
      .filter(Boolean),
  );
  const etiquette = (re) =>
    morceaux.findIndex((ms) => ms.some((m) => re.test(simple(m).replace(/\s+/g, ' '))));
  // Le premier nom sous l'étiquette : ni IBAN, ni autre étiquette, ni montant.
  const nomSous = (i) => {
    if (i < 0) return null;
    for (let j = i + 1; j < Math.min(lignes.length, i + 6); j += 1) {
      for (const m of morceaux[j]) {
        const s = simple(m).replace(/\s+/g, ' ');
        if (QR_CREANCIER.test(s) || QR_DEBITEUR.test(s) || QR_ETIQUETTE.test(s)) continue;
        if (/^(CH|LI)\d{2}[\s\dA-Z]{15,}$/i.test(m.replace(/\s/g, ' '))) continue;
        if ((m.match(/\p{L}/gu) || []).length < 3 || /^\d/.test(m)) continue;
        return m.slice(0, 80);
      }
    }
    return null;
  };
  let montant = null;
  const iMontant = etiquette(QR_MONTANT);
  if (iMontant >= 0) {
    for (
      let j = iMontant + 1;
      j < Math.min(lignes.length, iMontant + 4) && montant == null;
      j += 1
    ) {
      const m = lignes[j].match(/\b(?:CHF|EUR)\s+(\d{1,3}(?:['’ ]\d{3})*|\d+)[.,](\d{2})\b/);
      if (m) montant = Number(m[1].replace(/['’ ]/g, '') + '.' + m[2]);
    }
  }
  // L'adresse du créancier : la rue et le NPA qui suivent son nom.
  const iCreancier = etiquette(QR_CREANCIER);
  const creancier = nomSous(iCreancier);
  let rue = null;
  let localite = null;
  const iNom = creancier
    ? morceaux.findIndex((ms, j) => j > iCreancier && ms.includes(creancier))
    : -1;
  for (let j = iNom; iNom >= 0 && j < Math.min(lignes.length, iNom + 5); j += 1) {
    for (const m of morceaux[j]) {
      if (m === creancier) continue;
      if (!localite && /^(?:CH-)?[1-9]\d{3}\s+\p{L}/u.test(m)) localite = m.replace(/^CH-/, '');
      else if (
        !rue &&
        !localite &&
        RUE.test(simple(m)) &&
        /\p{L}{3}/u.test(m) &&
        !/^(CH|LI)\d{2}/.test(m)
      )
        rue = m;
    }
  }
  return {
    creancier,
    adresse: localite ? (rue ? rue + ', ' : '') + localite : null,
    debiteur: nomSous(etiquette(QR_DEBITEUR)),
    montant: montant > 0 ? montant : null,
  };
}

/**
 * Lit un ticket à partir de son texte.
 *
 * @param {string} texte texte du ticket, ligne par ligne
 * @param {{maintenant?: Date, nousMemes?: string[], pays?: string}} [options] date de référence ;
 *   nom de la société qui reçoit la facture, à ne pas prendre pour le fournisseur
 * @returns {{fournisseur: string|null, adresse: string|null, iban: string|null,
 *   numero: string|null, date: string|null, montantTtc: number|null, montantTva: number|null,
 *   tauxTva: number[], categorie: string|null, libelle: string,
 *   paiement: 'especes'|'carte'|null, confiance: number}}
 *   la lecture, à la forme de celle du Pack IA
 */
export function litTexteTicket(texte, { maintenant = new Date(), nousMemes = [], pays } = {}) {
  const brut = String(texte || '');
  const imprimees = brut.split(/\r?\n/).filter((l) => l.trim());
  const qr = bulletinQr(imprimees);
  // Celui qui paie le bulletin, c'est nous, même écrit autrement.
  const nous = qr.debiteur ? [...nousMemes, qr.debiteur] : nousMemes;
  const lignes = sansDestinataire(
    sansNousMemes(
      imprimees.map((l) => l.trim()),
      nous,
    ),
  );
  // Le montant du bulletin QR est celui à payer, rabais déduits.
  const lu = qr.montant ?? total(lignes);
  const leJour = date(brut, maintenant);
  const creancier = qr.creancier && !estNous(qr.creancier, nous) ? qr.creancier : null;
  const enTete = creancier ? null : sansParasites(fournisseur(lignes));
  const pied = creancier || enTete ? null : fournisseurDuPied(lignes);
  const nomBrut = creancier || enTete || (pied ? sansParasites(pied.nom) : null);
  const { adresse: brute } = adresse(lignes);
  const ouBrut = sansParasites(
    (creancier && qr.adresse) || (pied && adresseDuPied(pied.ligne)) || brute,
  );
  // « Sanitaire Exemple SA Rue du Lac 12 1207 Genève » sur une seule ligne :
  // le nom s'arrête à la forme juridique ou au début de la rue.
  const { nom, reste } = nomSansAdresse(nomBrut);
  const ou =
    reste && (!ouBrut || ouBrut.startsWith(nomBrut || '') || ouBrut.includes(nom)) ? reste : ouBrut;
  const tous = lignes.flatMap((l) => montantsDe(l));
  const { tauxTva, montantTva } = tva(lignes, lu, tauxAdmis(pays), tous);
  const montantTtc = totalSelonLaTva(lu, tauxTva, montantTva, tous);
  // Un seul taux imprimé, sa TVA illisible : celle du total à ce taux.
  const tvaDuTicket =
    montantTva == null && montantTtc != null && tauxTva.length === 1 && montantTtc > 0
      ? auCentime((montantTtc * tauxTva[0]) / (100 + tauxTva[0]))
      : montantTva;
  const cat = categorie(brut);
  // Le nom du commerce, tel qu'il est imprimé : la ville s'ajoutait au libellé.
  const libelle = (nom || '').slice(0, 60);
  const trouves = (montantTtc != null) + (leJour != null);
  return {
    fournisseur: nom,
    adresse: ou,
    iban: iban(brut),
    numero: numero(brut),
    date: leJour,
    montantTtc,
    montantTva: tvaDuTicket,
    tauxTva,
    categorie: cat,
    libelle,
    // Payé en espèces ou par carte, quand le ticket l'imprime.
    paiement: paiementDuTicket(lignes),
    confiance: trouves === 2 ? 0.7 : trouves === 1 ? 0.45 : 0.2,
  };
}

/**
 * Le texte qui revient le plus souvent, mot par mot : chaque lecture d'une
 * photo se trompe à sa façon (« ST ALIEN », « ST JULIEN », « PERLV »), et
 * deux lectures sur trois disent juste.
 *
 * @param {(string|null)[]} candidats ce que chaque lecture a donné
 * @returns {string|null} le texte retenu
 */
function consensus(candidats) {
  const lus = candidats.filter(Boolean).map((c) => String(c).split(/\s+/));
  if (!lus.length) return null;
  // Les lectures du même nombre de mots, le groupe le plus fourni ; à égalité,
  // celui de la première lecture.
  const groupes = new Map();
  lus.forEach((mots) => groupes.set(mots.length, [...(groupes.get(mots.length) || []), mots]));
  const groupe = [...groupes.values()].reduce((a, g) => (g.length > a.length ? g : a));
  return groupe[0]
    .map((premier, i) => {
      const votes = new Map();
      groupe.forEach((mots) => votes.set(mots[i], (votes.get(mots[i]) || 0) + 1));
      const plus = Math.max(...votes.values());
      return votes.get(premier) === plus ? premier : [...votes].find(([, n]) => n === plus)[0];
    })
    .join(' ');
}

/**
 * Lit un ticket à partir de plusieurs lectures de la même photo (lib/ocr-ticket.js).
 *
 * <p>Les montants, la date et la TVA se cherchent dans toutes les lectures
 * ensemble : le total se contrôle par la TVA imprimée, et ce qu'une lecture a
 * manqué, une autre l'a lu. Le nom du commerce et son adresse se votent mot
 * par mot.
 *
 * @param {string|string[]} textes le texte de chaque lecture
 * @param {object} [options] comme pour {@link litTexteTicket}
 * @returns {object} la lecture, à la forme de {@link litTexteTicket}
 */
export function litLesTextes(textes, options = {}) {
  const liste = [].concat(textes).filter((t) => String(t || '').trim());
  if (liste.length <= 1) return litTexteTicket(liste[0] || '', options);
  const ensemble = litTexteTicket(liste.join('\n'), options);
  const parLecture = liste.map((t) => litTexteTicket(t, options));
  const nom = consensus(parLecture.map((l) => l.fournisseur)) ?? ensemble.fournisseur;
  // Le vote mot à mot peut garder le tiret d'une lecture : « 286 -, 1258 ».
  const ou = (consensus(parLecture.map((l) => l.adresse)) ?? ensemble.adresse)?.replace(
    /\s+[-–=]+(?=,)/g,
    '',
  );
  return { ...ensemble, fournisseur: nom, adresse: ou, libelle: (nom || '').slice(0, 60) };
}
